<script setup>
import { ref, computed, onMounted } from 'vue'
import { api } from '../composables/useApi.js'
import ShareButtons from '../components/ShareButtons.vue'
import {
    formatSecondsToFullTime,
    formatTimestamp,
} from '../composables/useFormatting.js'

const origin = window.location.origin

const props = defineProps({
    username: {
        type: String,
        required: true,
    },
    totalPresses: {
        type: Number,
        required: true,
    },
    totalTimeFormatted: {
        type: String,
        required: true,
    },
    totalTimeClaimed: {
        type: Number,
        required: true,
    },
})

const presses = ref([])
const loading = ref(true)

const profileUrl = computed(() => `${origin}/user/${props.username}/`)

const profileShareText = computed(() => {
    const pressText = props.totalPresses === 1 ? 'press' : 'presses'
    return `Check out ${props.username}'s button press history: ${props.totalPresses} ${pressText} claiming ${props.totalTimeFormatted} of time! #thebutton`
})

const loadData = () => {
    api.get(
        `/api/buttonpresses/?owner__username=${props.username}&ordering=-timestamp`
    )
        .then((response) => {
            presses.value = response.data.results
        })
        .catch((error) => {
            console.error('Error fetching user data:', error)
        })
        .finally(() => {
            loading.value = false
        })
}

onMounted(() => {
    loadData()
})
</script>

<template>
    <div class="c-user-profile">
        <div class="c-user-profile__container">
            <!-- Breadcrumb navigation -->
            <nav class="c-breadcrumb">
                <a href="/" class="c-breadcrumb__link">Home</a>
                <span class="c-breadcrumb__separator">/</span>
                <span class="c-breadcrumb__current">{{ props.username }}</span>
            </nav>

            <div class="c-section-header">
                <h2 class="c-heading">Press History</h2>
                <p class="c-subheading">
                    A look at all button presses for {{ props.username }}
                </p>
            </div>

            <div v-if="loading" class="c-user-profile__loading">
                Loading History...
            </div>

            <div v-else>
                <div
                    v-if="props.totalPresses === 0"
                    class="c-user-profile__empty"
                >
                    No presses found for this user.
                </div>
                <div v-else>
                    <div class="c-user-profile__summary glass">
                        <div class="c-user-profile__stats">
                            <div class="c-user-profile__stat">
                                <span class="c-user-profile__stat-label"
                                    >Total Presses</span
                                >
                                <span class="c-user-profile__stat-value">{{
                                    props.totalPresses
                                }}</span>
                            </div>
                            <div class="c-user-profile__stat">
                                <span class="c-user-profile__stat-label"
                                    >Total Time Claimed</span
                                >
                                <span class="c-user-profile__stat-value">{{
                                    props.totalTimeFormatted
                                }}</span>
                            </div>
                        </div>
                        <div class="c-user-profile__share">
                            <ShareButtons
                                :url="profileUrl"
                                :text="profileShareText"
                                :show-label="true"
                            />
                        </div>
                    </div>

                    <div class="c-user-profile__table-wrapper">
                        <div class="leaderboard glass">
                            <table
                                class="leaderboard__table"
                                aria-label="User button presses"
                            >
                                <thead>
                                    <tr>
                                        <th scope="col">Time claimed</th>
                                        <th scope="col">Claimed at</th>
                                        <th scope="col">Tier</th>
                                        <th scope="col">Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="entry in presses"
                                        :key="entry.id"
                                    >
                                        <td class="c-user-profile__table-time">
                                            <a
                                                :href="`/user/${props.username}/press/${entry.id}/`"
                                                class="c-user-profile__press-link"
                                            >
                                                {{
                                                    formatSecondsToFullTime(
                                                        entry.elapsed_seconds
                                                    )
                                                }}
                                            </a>
                                        </td>
                                        <td class="c-user-profile__table-date">
                                            {{
                                                formatTimestamp(entry.timestamp)
                                            }}
                                        </td>
                                        <td>
                                            <span
                                                class="tier-badge"
                                                :class="`tier-${entry.tier.toLowerCase()}`"
                                            >
                                                {{ entry.tier_label }}
                                            </span>
                                        </td>
                                        <td>
                                            <ShareButtons
                                                :url="`${origin}/user/${props.username}/press/${entry.id}/`"
                                                :text="`Check out my button press that claimed ${formatSecondsToFullTime(entry.elapsed_seconds)} and achieved the ${entry.tier_label || entry.tier} tier! #thebutton`"
                                                :show-label="false"
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
